import moment from "moment";

export const utilNumberPhoneConvention = (number) => {
  const phonePattern = /^(\+?\d{1,4}[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?[\d-.\s]{7,15}$/;

  if (!phonePattern.test(number)) {
    return number;
  }

  let fN = "+";
  fN = fN + number.slice(0, 2) + " ";
  fN = fN + number.slice(2, 5) + "-";
  fN = fN + number.slice(5, 9) + "-";
  fN = fN + number.slice(9);

  return fN;
};

export const utilDateProcessing = (timestamp) => {
  const today = moment().format("DD/MM/YYYY");
  const compared = moment.unix(timestamp / 1000).format("DD/MM/YYYY");

  if (today === compared) {
    return new Date(Number(timestamp)).toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } else if (moment().subtract(1, "day").format("DD/MM/YYYY") === compared) {
    return "Yesterday";
  }
  return new Date(Number(timestamp)).toLocaleString("en-GB", {
    month: "numeric",
    year: "numeric",
    day: "numeric",
  });
};

export const combine2ChatNoDuplicate = (array1, array2) => {
  const combined = [
    ...array1,
    ...array2.filter((chatItem) => !array1.some((filteredItem) => filteredItem.id === chatItem.id)),
  ];

  return combined;
};

export const chatNoDuplicate = (array) => {
  const uniqueArray2 = array.filter((item, index, self) => {
    return index === self.findIndex((t) => t.id === item.id);
  });

  return uniqueArray2;
};

export const getRoleStatusField = (roleName) => {
  const roleMap = {
    SALES: "status",
    CS: "csStatus",
    "SUPER ADMIN": "superStatus",
    HEAD: "headStatus",
    AUDITOR: "auditorStatus",
    "SYSTEM ADMIN": "sysAdminStatus",
  };
  return roleMap[roleName] || "adminStatus";
};

export const chatMatchesActiveTab = (chat, activeTab, roleName) => {
  if (!activeTab || activeTab === "all") return true;
  const statusField = getRoleStatusField(roleName);
  return chat[statusField] === activeTab.toUpperCase();
};

/**
 * Checks if a chat matches all currently active filters.
 * Used to guard socket-incoming chats before adding them to the list.
 * @param {object} chat - chat object (must have tags and tickets populated)
 * @param {object} filters - { filterUserId, filterTag, filterCampaign, filterDepartment }
 */
export const chatMatchesActiveFilters = (
  chat,
  { filterUserId, filterTag, filterCampaign, filterDepartment, filterDate },
) => {
  if (filterUserId && chat?.userId !== filterUserId) {
    return false;
  }

  if (filterTag && filterTag.length > 0) {
    const chatTagIds = (chat?.tags || []).map((t) => t.id);
    const hasMatchingTag = filterTag.some((tagId) => chatTagIds.includes(tagId));
    if (!hasMatchingTag) return false;
  }

  if (filterCampaign && filterCampaign.length > 0) {
    const campaignId = chat?.tickets?.[0]?.campaignId ?? chat?.tickets?.[0]?.campaign?.id;
    if (!campaignId || !filterCampaign.includes(campaignId)) return false;
  }

  if (filterDepartment && filterDepartment.length > 0) {
    const departmentId = chat?.channel?.departmentId ?? chat?.channel?.department?.id;
    if (!departmentId || !filterDepartment.includes(departmentId)) return false;
  }

  if (filterDate && (filterDate.dateFrom || filterDate.dateTo)) {
    // Same field the date filter queries on the backend (chat.updatedAt).
    const chatDate = new Date(chat?.updatedAt || chat?.lastMessage?.createdAt || chat?.createdAt);
    if (filterDate.dateFrom && chatDate < new Date(filterDate.dateFrom)) return false;
    if (filterDate.dateTo && chatDate > new Date(filterDate.dateTo)) return false;
  }

  return true;
};

export const getUnprocessedMessages = (messages, processedCount) => {
  if (!Array.isArray(messages)) {
    return { toProcess: [], nextCount: processedCount };
  }

  const startIndex = messages.length < processedCount ? 0 : processedCount;

  return {
    toProcess: messages.slice(startIndex),
    nextCount: messages.length,
  };
};

/**
 * Removes a chat from both chatList and filteredChatList (when present).
 * A chat that fails a socket-guard check (own-role, department, filter) must
 * disappear from whichever list is currently rendered — chatList when idle,
 * filteredChatList when a text search is active — or it lingers as a leak.
 */
export const removeChatFromLists = (chatId, chatList, filteredChatList) => {
  const newChatList = chatNoDuplicate(chatList.filter((c) => c.id !== chatId));
  const newFilteredChatList = filteredChatList.some((c) => c.id === chatId)
    ? chatNoDuplicate(filteredChatList.filter((c) => c.id !== chatId))
    : filteredChatList;

  return { newChatList, newFilteredChatList };
};

export const addChatWithoutDuplication = (newChat, chatList) => {
  if (!newChat || !newChat.id) {
    console.error("New chat must have an id property");
    return chatList;
  }

  // Create a copy of the list to avoid mutating the original
  const updatedList = [...chatList];

  // Find the index of any existing chat with the same ID
  const existingIndex = updatedList.findIndex((chat) => chat.id === newChat.id);

  // If found, remove the existing chat
  if (existingIndex !== -1) {
    updatedList.splice(existingIndex, 1);
  }

  // Add the new chat at the beginning
  updatedList.unshift(newChat);

  return updatedList;
};

// Insert/replace an incoming socket message and keep the list ordered by
// timestamp — same order the backend returns — so a late-delivered older
// message lands in its date group instead of at the bottom.
export const mergeIncomingMessage = (messages, incoming) =>
  [...messages.filter((msg) => msg.id !== incoming.id), incoming].sort(
    (a, b) => parseInt(a.timestamp) - parseInt(b.timestamp)
  );
